import {
    FormControlLabel, Grid, Radio, RadioGroup
} from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { SubmitButton } from '@tassat/button';
import { TransactionStatusCode, TransactionTypeChar, getDeclineReasons } from '@tassat/common-definitions';
import { TransactionHistoryRecord } from '@tassat/fred-extended-definitions';
import React from 'react';

import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';

interface DeclineTransactionProps extends WithStyles<typeof muiStyles> {
    selectedTransaction: TransactionHistoryRecord;
    isCancel: boolean;
    declineTransaction: (requestId: string, reason: string, isCancel: boolean) => void;
    declineFailedTransaction: (transaction: TransactionHistoryRecord) => void;
    cancelDecline: () => void;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    root: { padding: '0 2.5rem' },
    promptText: {
        fontSize: '1.5rem',
        padding: '1rem 0'
    },
    radioGroup: {
        width: '100%',
        marginBottom: '2rem'
    },
    radioContainer: { margin: '0 auto' },
    radioColumn: { width: 'auto' as const },
    radio: {
        color: theme?.app?.applicationColors.enumColors.greyLight ||
            '#a7a7a7',
        '&.Mui-checked': {
            color: theme?.app?.applicationColors.failureDark ||
                '#AD3D4B'
        }
    },
    checked: {},
    cancelMargin: { marginTop: '2rem' },
    cancelButton: {
        cursor: 'pointer' as const,
        fontSize: '1.2rem',
        lineHeight: '1.2rem',
        color: theme?.app?.clientTheme.colors.primaryColor ||
            '#375073',
        padding: '.3rem',
        marginTop: '1rem',
        '&:hover': {
            backgroundColor: theme?.app?.clientTheme.colors.buttons.primaryHover ||
                '#bec4cf'
        }
    }
});

function DeclineTransactionComponent(props: DeclineTransactionProps) {
    const {
        classes,
        selectedTransaction,
        isCancel,
        declineTransaction,
        declineFailedTransaction,
        cancelDecline
    } = props;

    const [ declineReason, setDeclineReason ] = React.useState<string>('');
    const transactionDeclineReasons = React.useMemo(() => {
        return getDeclineReasons(selectedTransaction.type as TransactionTypeChar);
    }, [selectedTransaction.type]);
    // Split reasons in two for two column
    const [ leftCol, rightCol ] = React.useMemo(() => {
        const midIndex = Math.ceil(transactionDeclineReasons.length / 2);
        const leftCol = transactionDeclineReasons.slice(0, midIndex);
        const rightCol = transactionDeclineReasons.slice(midIndex, transactionDeclineReasons.length);

        return [ leftCol, rightCol ];
    }, [transactionDeclineReasons]);

    return (
        <Grid
            alignItems='center'
            className={classes.root}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            {!isCancel && (
                <>
                    <span className={classes.promptText}>Select a reason for {isCancel ? 'canceling' : 'declining'} this transaction:</span>
                    <RadioGroup
                        aria-label='decline-reason'
                        className={classes.radioGroup}
                        name='decline-reason'
                        onChange={(e) => setDeclineReason(e.target.value)}
                        value={declineReason}
                    >
                        <Grid
                            className={classes.radioContainer}
                            container
                            direction='row'
                            item
                            justifyContent='space-around'
                            spacing={0}
                            wrap='nowrap'
                            xs={8}
                        >
                            <Grid
                                className={classes.radioColumn}
                                container
                                direction='column'
                                item
                                spacing={0}
                                wrap='nowrap'
                            >
                                {leftCol.map(declineReason => (
                                    <FormControlLabel
                                        control={<Radio classes={{
                                            root: classes.radio,
                                            checked: classes.checked
                                        }}
                                        />}
                                        key={declineReason.code}
                                        label={declineReason.reason}
                                        value={declineReason.code}
                                    />
                                ))}
                            </Grid>
                            <Grid
                                className={classes.radioColumn}
                                container
                                direction='column'
                                item
                                spacing={0}
                                wrap='nowrap'
                            >
                                {rightCol.map(declineReason => (
                                    <FormControlLabel
                                        control={<Radio classes={{
                                            root: classes.radio,
                                            checked: classes.checked
                                        }}
                                        />}
                                        key={declineReason.code}
                                        label={declineReason.reason}
                                        value={declineReason.code}
                                    />
                                ))}
                            </Grid>
                        </Grid>
                    </RadioGroup>
                </>
            )}
            {selectedTransaction.Status === TransactionStatusCode.BANK_NO_ACK &&
                <span className={classes.promptText}>Are you sure you want to decline this failed transaction?</span>}
            <SubmitButton
                className={isCancel ? classes.cancelMargin : ''}
                disabled={!declineReason && !isCancel}
                onClick={() => {
                    if (selectedTransaction.type === TransactionTypeChar.order) {
                        declineTransaction(selectedTransaction.LinkTransRequestId, declineReason, isCancel);
                    } else {
                        declineTransaction(selectedTransaction.RequestId, declineReason, isCancel);
                    }
                }}
                type={isCancel ? 'submit' : 'decline'}
            />
            <span
                className={classes.cancelButton}
                onClick={cancelDecline}
            >
                CANCEL
            </span>
        </Grid>
    );
}

const DeclineTransaction = withStyles(muiStyles)(DeclineTransactionComponent);
export { DeclineTransaction };